import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

const STATUS_CONFIG = {
  captured:   { bg: 'rgba(22,163,74,0.10)',  color: '#16a34a', border: 'rgba(22,163,74,0.25)',  label: 'Paid',       icon: 'CheckCircle2' },
  authorized: { bg: 'rgba(0,74,173,0.10)',   color: '#004AAD', border: 'rgba(0,74,173,0.25)',   label: 'Authorized', icon: 'Clock'        },
  pending:    { bg: 'rgba(245,158,11,0.10)', color: '#d97706', border: 'rgba(245,158,11,0.25)', label: 'Pending',    icon: 'Clock'        },
  failed:     { bg: 'rgba(220,38,38,0.10)',  color: '#dc2626', border: 'rgba(220,38,38,0.25)',  label: 'Failed',     icon: 'XCircle'      },
  refunded:   { bg: 'rgba(100,116,139,0.10)',color: '#64748b', border: 'rgba(100,116,139,0.25)',label: 'Refunded',   icon: 'RotateCcw'    },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 10px', borderRadius: 100,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
      whiteSpace: 'nowrap',
    }}>
      <Icon name={cfg.icon} size={11} color={cfg.color} />
      {cfg.label}
    </span>
  );
};

const formatCurrency = (amount, currency) => {
  const symbol = currency === 'INR' ? '₹' : '$';
  return `${symbol}${(amount / 100)?.toFixed(2)}`;
};

const InvoiceHistoryTable = ({ paymentHistory, isLoading, onDownloadInvoice, onBulkExport, userProfile }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredPayments = paymentHistory?.filter(payment => {
    const matchesSearch =
      payment?.razorpay_payment_id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      payment?.razorpay_order_id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment?.payment_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasExportable = paymentHistory?.filter(p => p?.payment_status === 'captured')?.length > 0;

  return (
    <div style={{
      background: 'var(--card, #ffffff)',
      border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 16, padding: '24px 24px 28px',
      fontFamily: 'inherit',
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{
            fontFamily: "'Sansation', sans-serif",
            fontSize: 18, fontWeight: 700,
            color: 'var(--foreground, #0f172a)',
            margin: '0 0 4px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon name="FileText" size={18} color="#004AAD" />
            Invoice History
          </h2>
          <p style={{ fontFamily: 'inherit', fontSize: 13, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
            View and download all your invoices
          </p>
        </div>

        {/* Export All — blue gradient */}
        <button
          onClick={onBulkExport}
          disabled={!hasExportable}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '9px 18px', borderRadius: 9,
            border: 'none', cursor: hasExportable ? 'pointer' : 'not-allowed',
            background: hasExportable ? GRAD : 'rgba(0,74,173,0.12)',
            color: hasExportable ? '#ffffff' : 'rgba(0,74,173,0.45)',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            boxShadow: hasExportable ? GRAD_SHADOW : 'none',
            opacity: hasExportable ? 1 : 0.6,
            transition: 'filter 0.15s, transform 0.15s',
          }}
          onMouseEnter={e => { if (hasExportable) { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
        >
          <Icon name="Download" size={14} color={hasExportable ? '#fff' : 'rgba(0,74,173,0.45)'} />
          Export All
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Icon name="Search" size={15} color="var(--muted-foreground, #94a3b8)" style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }} />
          <input
            type="text"
            placeholder="Search by transaction ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            style={{
              width: '100%', paddingLeft: 36, paddingRight: 14,
              paddingTop: 9, paddingBottom: 9,
              background: 'var(--background, #f8fafc)',
              border: '1px solid var(--border, #e2e8f0)',
              borderRadius: 9, fontSize: 13,
              color: 'var(--foreground, #0f172a)',
              fontFamily: 'inherit', outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.currentTarget.style.borderColor = '#004AAD'}
            onBlur={e => e.currentTarget.style.borderColor = 'var(--border, #e2e8f0)'}
          />
        </div>
        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e?.target?.value)}
          style={{
            padding: '9px 14px',
            background: 'var(--background, #f8fafc)',
            border: '1px solid var(--border, #e2e8f0)',
            borderRadius: 9, fontSize: 13,
            color: 'var(--foreground, #0f172a)',
            fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => e.currentTarget.style.borderColor = '#004AAD'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border, #e2e8f0)'}
        >
          <option value="all">All Status</option>
          <option value="captured">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 10 }}>
          <Icon name="Loader2" size={22} color="#004AAD" className="animate-spin" />
          <span style={{ fontFamily: 'inherit', fontSize: 14, color: 'var(--muted-foreground, #64748b)' }}>Loading invoices...</span>
        </div>
      ) : filteredPayments?.length > 0 ? (
        <>
          {/* Desktop table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr style={{ background: 'var(--muted, #f8fafc)' }}>
                  {['Invoice', 'Date', 'Amount', 'Payment Method', 'Status', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '12px 16px',
                      fontSize: 11, fontWeight: 700,
                      color: 'var(--muted-foreground, #64748b)',
                      textAlign: i === 5 ? 'right' : 'left',
                      fontFamily: 'inherit',
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayments?.map((payment, idx) => (
                  <tr
                    key={payment?.id}
                    style={{
                      borderBottom: idx < filteredPayments.length - 1 ? '1px solid var(--border, #e2e8f0)' : 'none',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--muted, #f8fafc)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Invoice */}
                    <td style={{ padding: '14px 16px' }}>
                      <p style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--foreground, #0f172a)', margin: '0 0 3px' }}>
                        Paid Deck Audit
                      </p>
                      <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
                        {payment?.razorpay_payment_id?.slice(0, 20) || payment?.razorpay_order_id?.slice(0, 20)}
                      </p>
                    </td>
                    {/* Date */}
                    <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--foreground, #0f172a)', fontFamily: 'inherit' }}>
                      {new Date(payment?.paid_at || payment?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    {/* Amount */}
                    <td style={{ padding: '14px 16px' }}>
                      <p style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--foreground, #0f172a)', margin: '0 0 2px' }}>
                        {formatCurrency(payment?.amount, payment?.currency)}
                      </p>
                      <p style={{ fontFamily: 'inherit', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
                        {payment?.currency}
                      </p>
                    </td>
                    {/* Method */}
                    <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--muted-foreground, #64748b)', fontFamily: 'inherit' }}>
                      {payment?.payment_method || 'Razorpay'}
                    </td>
                    {/* Status */}
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge status={payment?.payment_status} />
                    </td>
                    {/* Download */}
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      {payment?.payment_status === 'captured' && (
                        <button
                          onClick={() => onDownloadInvoice(payment)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 12px', borderRadius: 8,
                            border: '1px solid var(--border, #e2e8f0)',
                            background: 'transparent', cursor: 'pointer',
                            fontSize: 12, fontWeight: 600,
                            color: 'var(--foreground, #0f172a)',
                            fontFamily: 'inherit',
                            transition: 'border-color 0.15s, color 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#004AAD'; e.currentTarget.style.color = '#004AAD'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border, #e2e8f0)'; e.currentTarget.style.color = 'var(--foreground, #0f172a)'; }}
                        >
                          <Icon name="Download" size={13} />
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div style={{ display: 'none' }} className="billing-mobile-cards">
            {filteredPayments?.map((payment) => (
              <div key={payment?.id} style={{
                background: 'var(--muted, #f8fafc)',
                borderRadius: 12, padding: 16,
                border: '1px solid var(--border, #e2e8f0)',
                marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--foreground, #0f172a)', margin: '0 0 3px' }}>
                      Paid Deck Audit
                    </p>
                    <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
                      {payment?.razorpay_payment_id?.slice(0, 20) || payment?.razorpay_order_id?.slice(0, 20)}
                    </p>
                  </div>
                  <StatusBadge status={payment?.payment_status} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <p style={{ fontFamily: 'inherit', fontSize: 10, color: 'var(--muted-foreground, #64748b)', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Date</p>
                    <p style={{ fontFamily: 'inherit', fontSize: 13, color: 'var(--foreground, #0f172a)', margin: 0 }}>
                      {new Date(payment?.paid_at || payment?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontFamily: 'inherit', fontSize: 10, color: 'var(--muted-foreground, #64748b)', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Amount</p>
                    <p style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--foreground, #0f172a)', margin: 0 }}>
                      {formatCurrency(payment?.amount, payment?.currency)}
                    </p>
                  </div>
                </div>
                {payment?.payment_status === 'captured' && (
                  <button
                    onClick={() => onDownloadInvoice(payment)}
                    style={{
                      width: '100%', padding: '10px 0', borderRadius: 9,
                      border: 'none', cursor: 'pointer',
                      background: GRAD, color: '#ffffff',
                      fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                      boxShadow: GRAD_SHADOW,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    }}
                  >
                    <Icon name="Download" size={14} color="#fff" />
                    Download Invoice
                  </button>
                )}
              </div>
            ))}
          </div>
          {/* Mobile visibility via media query */}
          <style>{`
            @media (max-width: 1023px) {
              .billing-mobile-cards { display: block !important; }
              .billing-desktop-table { display: none !important; }
            }
          `}</style>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Icon name="FileText" size={44} color="var(--muted-foreground, #94a3b8)" style={{ marginBottom: 14 }} />
          <h3 style={{
            fontFamily: "'Sansation', sans-serif",
            fontSize: 17, fontWeight: 700,
            color: 'var(--foreground, #0f172a)',
            margin: '0 0 6px',
          }}>
            No invoices found
          </h3>
          <p style={{ fontFamily: 'inherit', fontSize: 13, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Your invoices will appear here after making payments'}
          </p>
        </div>
      )}
    </div>
  );
};

export default InvoiceHistoryTable;